import Loader from 'component/loader_component';
import { LoaderComponent } from 'component/loader_component/style';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { getThemeBrandings, setBrandingData, setDefaultTheme } from 'redux/slices/themeSlice';

export default function ThemeProvider() {
  const dispatch = useDispatch();
  const [isRendered, setIsRendered] = useState(false);
  const [currentThemeData, setCurrentThemeData] = useState(null);
  const [themeFolderName, setThemeFolderName] = useState('globlue');
  const [isLoading, setIsLoading] = useState(true);
  const [stylesApplied, setStylesApplied] = useState(false);
  useEffect(() => {
    const fetchThemeDetails = async () => {
      try {
        let themeName;
        const { hostname, port } = window.location;
        const currentDomain = `${hostname}${port ? `:${port}` : ''}`;

        const brandingTheme = await dispatch(getThemeBrandings());
        const themesData = brandingTheme?.payload?.data;

        if (themesData.length === 0) {
          await dispatch(setDefaultTheme('globlue'));
          themeName = 'globlue';
        } else {
          const themeObject = themesData.find((theme) => theme.url === currentDomain);
          themeName = themeObject ? themeObject.themeName : themesData[0]?.themeName;
          setThemeFolderName(themeName);
          await dispatch(
            setDefaultTheme(themeObject ? themeObject.defaultTheme.toLowerCase() : themesData[0]?.defaultTheme)
          );
          await dispatch(setBrandingData(themeObject ? themeObject : themesData[0]));
        }
        if (themeName) {
          // Fetch theme data using the fetched theme name
          const response = await fetch(`/themes/${themeName}/themedata.json?_=${new Date().getTime()}`, {
            headers: {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
              Expires: '0',
            },
          });
          if (!response.ok) {
            throw new Error('Failed to fetch theme data');
          }
          const themeData = await response.json();
          setCurrentThemeData(themeData);
        }
      } catch (error) {
        return error;
      } finally {
        setIsLoading(false);
      }
    };

    fetchThemeDetails();
  }, []);

  const renderDynamicStyles = () => {
    if (!currentThemeData) return;

    const root = document.documentElement;
    //images
    root.style.setProperty(
      '--bg-login-background-image',
      `url(${encodeURI(`${currentThemeData.images.background_image}?cache=${new Date().getTime()}`)})`
    );
    root.style.setProperty('--show-background-image', currentThemeData.showBackGroundImage);
    root.style.setProperty(
      '--img-login-logo',
      `url(${encodeURI(`${currentThemeData.images.login_logo}?cache=${new Date().getTime()}`)})`
    );
    root.style.setProperty(
      '--img-afterlogin_logo',
      `url(${encodeURI(`${currentThemeData.images.afterlogin_logo}?cache=${new Date().getTime()}`)})`
    );
    root.style.setProperty(
      '--img-favicon',
      `url(${encodeURI(`${currentThemeData.images.favicon}?cache=${new Date().getTime()}`)})`
    );

    //login
    root.style.setProperty('--bg-login-background-color', currentThemeData.colors.login.background_color);
    root.style.setProperty('--bg-login-button-color', currentThemeData.colors.login.button_color);
    root.style.setProperty('--login-button-text-color', currentThemeData.colors.login.button_text_color);
    //Afterlogin
    root.style.setProperty('--bg-afterlogin-background-color', currentThemeData.colors.afterlogin.background_color);
    //leftmenu
    root.style.setProperty('--bg-leftmenu-panel-color', currentThemeData.colors.leftmenu.panel_color);
    root.style.setProperty('--leftmenu-text-color', currentThemeData.colors.leftmenu.text_color);
    root.style.setProperty('--leftmenu-active-color', currentThemeData.colors.leftmenu.active_color);
    root.style.setProperty('--leftmenu-highlighter-color', currentThemeData.colors.leftmenu.highlighter_color);
    //topmenu
    root.style.setProperty('--topmenu-hover-color', currentThemeData.colors.topmenu.hover_color);
    root.style.setProperty('--topmenu-text-color', currentThemeData.colors.topmenu.text_color);
    root.style.setProperty('--topmenu-active-color', currentThemeData.colors.topmenu.active_color);
    root.style.setProperty('--topmenu-highlighter-color', currentThemeData.colors.topmenu.highlighter_color);
    //button
    root.style.setProperty('--bg-button-default-color', currentThemeData.colors.button.default_color);
    root.style.setProperty('--button-text-color', currentThemeData.colors.button.text_color);
    root.style.setProperty('--button-hover-color', currentThemeData.colors.button.hover_color);
    setStylesApplied(true);
  };

  useEffect(() => {
    if (!isRendered && currentThemeData) {
      renderDynamicStyles();
      let favIconlink = document.querySelector("link[rel~='icon']");
      favIconlink.href = currentThemeData.images.favicon + `?cache=${new Date().getTime()}`;

      let stylesheetlink = document.createElement('link');
      stylesheetlink.rel = 'stylesheet';
      stylesheetlink.href = encodeURI(`themes/${themeFolderName}/style.css?cache=${new Date().getTime()}`);
      document.head.appendChild(stylesheetlink);

      let footer = document.createElement('footer');
      fetch(`themes/${themeFolderName}/footer.html?cache=${new Date().getTime()}`, {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
      })
        .then((response) => response.text())
        .then((data) => {
          footer.innerHTML = data;
          document.body.appendChild(footer);
          footer.classList.add('footer');
          applyClassNames();
        })
        .catch((error) => error);

      setIsRendered(true);
    }
  }, [currentThemeData]);

  const applyClassNames = () => {
    const footer = document.querySelector('footer');
    const isLogin = !!localStorage.getItem('role');
    const [addClass, removeClass] = isLogin
      ? ['after-login-footer', 'before-login-footer']
      : ['before-login-footer', 'after-login-footer'];

    footer.classList.add(addClass);
    footer.classList.remove(removeClass);

    if (footer.innerHTML.trim() && !/^<[^>]+><\/[^>]+>$/.test(footer.innerHTML.trim())) {
      const rootStyle = document.getElementById('root').style;
      rootStyle.height = isLogin ? 'auto' : '95%';
      footer.style.height = isLogin ? 'auto' : '5%';
    }
  };
  window.addEventListener('loginStateChange', applyClassNames);

  if (isLoading || !stylesApplied) {
    return (
      <LoaderComponent>
        <Loader />
      </LoaderComponent>
    );
  }
  return (
    <div className='theme-container'>
      <Outlet />
    </div>
  );
}
